import { Button, Grid, Paper } from '@mui/material'
import { CheckCircle } from '@mui/icons-material'
import { Field, Form, Formik, FormikHelpers } from 'formik'
import { CheckboxWithLabel } from 'formik-mui'
import { ClipboardOutline } from 'mdi-material-ui'
import { inject } from 'mobx-react'
import * as React from 'react'
import { Helmet } from 'react-helmet'
import { useParams } from 'react-router-dom'
import * as Yup from 'yup'
import CardWithTitle from '../common/CardWithTitle/CardWithTitle'
import ESDataTableWithHeader from '../common/ESDataTableWithHeader'
import FullWidthField from '../common/FullWidthField'
import IDataTableManager from '../common/IDataTableManager'
import ResponsiveAppBar from '../common/ResponsiveAppBar'
import SelectField from '../common/SelectField/SelectField'
import { noWhitespaceString } from '../common/YupExtensions'
import {
  DefaultAppointmentTask,
  DefaultComponent,
  IAppointmentTask,
  IComponent,
  IOrganization,
} from '../Definitions'
import { IStores } from '../Stores'
import AppointmentTaskDialog from './AppointmentTaskDialog'
import {
  createIComponentFormValues,
  IComponentFormValues,
  toIComponent,
} from './ComponentFormValues'

const ComponentSchema = Yup.object().shape({
  name: noWhitespaceString('Name is required', true),
  organizationId: Yup.string().required('Organization is required'),
})

interface IEditComponentState {
  reload: boolean
}
interface IEditComponentProps {
  allAppointmentTasks?: IAppointmentTask[]
  componentId?: string
  clearAppointmentTasks: () => void
  component?: IComponent
  componentEditFinished?: () => void
  currentAppointmentTaskCount?: number
  dataTableManager?: IDataTableManager<IAppointmentTask>
  getAllAppointmentTasks?: () => Promise<void>
  getColumnSettingsAndAppointmentTasks?: () => void
  getComponentById?: (id: string) => Promise<void>
  getOrganizations: () => Promise<void>
  isLoading?: boolean
  organizations?: IOrganization[]
  openDialog?: (appointmentTask: IAppointmentTask) => void
  rerouteToPath?: (path: string) => void
  saveComponent?: (component: IComponent) => Promise<void>
  setSelectedComponent?: (component: IComponent | undefined) => void
  currentAppOrganization?: IOrganization
}

class EditComponent extends React.Component<IEditComponentProps, IEditComponentState> {
  constructor(props: IEditComponentProps) {
    super(props)
    this.state = {
      reload: false,
    }
  }
  private async setup() {
    if (this.props.componentId) {
      await this.props.getComponentById!(this.props.componentId)
    } else {
      this.props.setSelectedComponent!(DefaultComponent(true))
    }
    this.props.getColumnSettingsAndAppointmentTasks!()
    this.props.getOrganizations!()
  }

  public async componentDidMount() {
    await this.setup()
  }

  public async componentDidUpdate(prevProps: IEditComponentProps) {
    if (this.props.componentId !== prevProps.componentId) {
      await this.setup()
    }
  }

  public componentWillUnmount() {
    this.props.setSelectedComponent!(undefined)
    this.props.clearAppointmentTasks!()
  }

  private cancel = () => {
    this.props.rerouteToPath!('/admin/components')
  }

  public handleSave = (submitForm: () => void, reload: boolean) => () => {
    this.setState({ reload }, () => {
      submitForm()
    })
  }

  public save = (
    values: IComponentFormValues,
    formikBag: FormikHelpers<IComponentFormValues>
  ) => {
    const component = toIComponent(values, this.props.component)

    this.props.saveComponent!(component).finally(() => {
      formikBag.setSubmitting(false)
      if (this.state.reload) {
        this.props.rerouteToPath!(`/admin/components/component/${component.id}`)
        this.setup()
      } else {
        this.props.rerouteToPath!('/admin/components')
      }
    })
  }

  public openDialogWithAppointment = () => () => {
    const appointmentTask = DefaultAppointmentTask(true)
    appointmentTask.componentId = this.props.component!.id
    this.props.openDialog!(appointmentTask)
  }

  public getOrganizationName = (organization: IOrganization) => {
    return organization.name
  }

  public getOrganizationValue = (organization: IOrganization) => {
    return organization.id
  }

  public render() {
    const { component } = this.props
    const initialValues = createIComponentFormValues(component)
    const title = component && component.isNew ? 'Add New Component' : 'Edit Component'

    return (
      <Grid>
        <Helmet>
          <title>{title}</title>
        </Helmet>
        {component ? (
          <Formik
            initialValues={initialValues}
            onSubmit={this.save}
            validationSchema={ComponentSchema}
            enableReinitialize={true}
          >
            {({ isValid, values, submitForm }) => (
              <Form>
                {/* TODO: Replace Prompt with useBlocker hook in functional component wrapper */}
                <ResponsiveAppBar title={title} pageIcon={<ClipboardOutline />}>
                  <Grid container direction="row" spacing={2} justifyContent="flex-end">
                    <Grid item>
                      <Button
                        size="small"
                        variant="contained"
                        style={{ backgroundColor: '#e0e0e0', color: '#000' }}
                        onClick={this.cancel}
                      >
                        Cancel
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button
                        variant="contained"
                        color="secondary"
                        size="small"
                        disabled={!isValid}
                        onClick={this.handleSave(submitForm, true)}
                      >
                        Save
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button
                        color="secondary"
                        size="small"
                        variant="contained"
                        disabled={!isValid}
                        onClick={this.handleSave(submitForm, false)}
                      >
                        Save and Close
                      </Button>
                    </Grid>
                  </Grid>
                </ResponsiveAppBar>

                <Grid container direction="row" spacing={3} sx={{ mt: 2, px: 2 }}>
                  <Grid container lg={6} md={12} item spacing={1}>
                    <CardWithTitle title="Component" icon={ClipboardOutline}>
                      <Grid container xs={12} spacing={1}>
                        <Grid item xs={6}>
                          <FullWidthField
                            variant="outlined"
                            type="outlined"
                            name="name"
                            label="Name"
                            disabled={values.isLocked}
                            required
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <SelectField
                            inputId="organizationIdUpdate"
                            getName={this.getOrganizationName}
                            getValue={this.getOrganizationValue}
                            items={this.props.organizations}
                            label="Organization"
                            name="organizationId"
                            fullWidth
                            outlined={true}
                            shrinkLabel={true}
                            required
                            disabled={values.isLocked || !this.props.component!.isNew}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Field
                            name="isDefaultComponent"
                            Label={{
                              label: 'Default Component',
                            }}
                            component={CheckboxWithLabel}
                            disabled={values.isLocked}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Field
                            name="isSimpleComponent"
                            Label={{
                              label: 'Simple Event',
                            }}
                            component={CheckboxWithLabel}
                            disabled={
                              values.isLocked ||
                              this.props.currentAppointmentTaskCount! > 0
                            }
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Field
                            name="excludeFromBundle"
                            Label={{
                              label: 'Exclude From Bundle',
                            }}
                            component={CheckboxWithLabel}
                            disabled={values.isLocked}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Field
                            name="excludeFromPTCount"
                            Label={{
                              label: 'Exclude From PT Count',
                            }}
                            component={CheckboxWithLabel}
                            disabled={values.isLocked}
                          />
                        </Grid>
                      </Grid>
                    </CardWithTitle>
                  </Grid>
                  {component && !component.isNew ? (
                    <Grid item lg={6} md={12} spacing={1}>
                      <Paper sx={{ p: 2, width: '100%' }}>
                        <ESDataTableWithHeader
                          dataTableManager={this.props.dataTableManager!}
                          addButtonOnClick={this.openDialogWithAppointment()}
                          title="Appointment Tasks"
                          icon={CheckCircle}
                          enableShowInactives={true}
                          padding={0}
                          elevation={0}
                          isAddButtonDisabled={values.isSimpleComponent}
                        />
                        <AppointmentTaskDialog />
                      </Paper>
                    </Grid>
                  ) : (
                    <></>
                  )}
                </Grid>
              </Form>
            )}
          </Formik>
        ) : (
          <></>
        )}
      </Grid>
    )
  }
}

const InjectedEditComponent = inject<
  IStores,
  IEditComponentProps,
  Partial<IEditComponentProps>,
  any
>((stores: IStores) => ({
  allAppointmentTasks: stores.appointmentTasks.appointmentTasks,
  clearAppointmentTasks: stores.appointmentTasks.clearAppointmentTaskData,
  component: stores.components.selectedComponent,
  componentEditFinished: stores.components.componentEditFinished,
  currentAppOrganization: stores.global.currentAppOrganization,
  currentAppointmentTaskCount: stores.appointmentTasks.appointmentTaskCount,
  dataTableManager: stores.appointmentTasks.dataTableStore,
  getAllAppointmentTasks: stores.appointmentTasks.getAllAppointmentTasks,
  getColumnSettingsAndAppointmentTasks:
    stores.appointmentTasks.getColumnSettingsAndAppointmentTasks,
  getComponentById: stores.components.getComponentById,
  getOrganizations: stores.organizations.getAllOrganizations,
  isLoading: stores.components.isLoading,
  openDialog: stores.appointmentTasks.openDialog,
  organizations: stores.organizations.organizations,
  rerouteToPath: stores.global.rerouteToPath,
  saveComponent: stores.components.saveComponent,
  setSelectedComponent: stores.components.setSelectedComponent,
}))(EditComponent)

// Wrapper to inject route params
const EditComponentWithRouter = () => {
  const { componentId } = useParams<{ componentId: string }>()
  return (
    <InjectedEditComponent
      componentId={componentId}
      clearAppointmentTasks={() => {}}
      getOrganizations={async () => {}}
    />
  )
}

export default EditComponentWithRouter
